import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ProjectOverlay from "./ProjectOverlay";
import "./WorkSection.css";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Static project data (non-translatable)
const PROJECTS_STATIC = [
  {
    id: 1,
    key: "moca",
    title: "MOCA",
    images: [
      "moca/moca-1.png",
      "moca/moca-2.png",
      "moca/moca-3.png",
      "moca/moca.mp4",
    ],
    link: "https://innovence.de/air-cat",
  },
  {
    id: 2,
    key: "pyure",
    title: "PYURE",
    images: [
      "pyure/pyure-3.png",
      "pyure/pyure-2.png",
      "pyure/pyure-1.png",
      "pyure/pyure.mp4",
    ],
    link: "https://pyure.ai/",
  },
  {
    id: 3,
    key: "bloom",
    title: "Bloom",
    images: [
      "bloom/bloom-1.png",
      "bloom/bloom-2.png",
      "bloom/bloom-3.png",
      "bloom/bloom-4.png",
      "bloom/bloom.mp4",
    ],
    link: "https://github.com/Alfonsomckenzy/Bloom",
  },
  {
    id: 4,
    key: "xtyz",
    title: "xtyz.de",
    images: ["xtyz/xtyz-1.png", "xtyz/xtyz-2.png", "xtyz/xtyz.mp4"],
    link: "https://xtyz.de/",
  },
];

// Simple fade-in label component
const FadeInLabel = ({ text, isActive }) => {
  if (!isActive) return null;

  return <span className="fade-in-text">{text}</span>;
};

// Helper function to check if a file is a video
const isVideoFile = (src) => {
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
  return videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));
};

// Helper function to get limited media (max 1: video if available, otherwise image)
const getLimitedMedia = (images) => {
  const video = images.find((src) => isVideoFile(src));
  const image = images.find((src) => !isVideoFile(src));

  // Return video if available, otherwise return image, otherwise empty array
  if (video) return [video];
  if (image) return [image];
  return [];
};

const WorkStackingCards = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  // Build PROJECTS array with translations
  const PROJECTS = PROJECTS_STATIC.map((project) => ({
    ...project,
    subtitle: t(`work.projects.${project.key}.subtitle`),
    category: t(`work.projects.${project.key}.category`),
    description: t(`work.projects.${project.key}.description`),
  }));

  const cardsRef = useRef([]);
  const [cardStates, setCardStates] = useState(
    PROJECTS_STATIC.map(() => ({ isMinimized: false })),
  );
  const [activeProject, setActiveProject] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );

  // Open overlay with a specific project
  const openOverlay = (project) => {
    setActiveProject(project);
  };

  // Close overlay
  const closeOverlay = () => {
    setActiveProject(null);
  };

  // Toggle 'overlay-open' class on #work section to hide its border
  useEffect(() => {
    const workSection = document.getElementById("work");
    if (workSection) {
      if (activeProject) {
        workSection.classList.add("overlay-open");
      } else {
        workSection.classList.remove("overlay-open");
      }
    }
    return () => {
      // Cleanup: remove class when component unmounts
      if (workSection) {
        workSection.classList.remove("overlay-open");
      }
    };
  }, [activeProject]);

  // Listen for closeOverlay event from sidebar navigation
  useEffect(() => {
    const handleCloseOverlay = () => {
      setActiveProject(null);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("closeOverlay", handleCloseOverlay);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("closeOverlay", handleCloseOverlay);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Ref to track previous card states for comparison (prevents unnecessary re-renders)
  const prevStatesRef = useRef(null);

  useLayoutEffect(() => {
    // Mobile detection for performance optimizations
    const checkIsMobile = window.innerWidth <= 768;

    // Skip animations on mobile - just show static cards
    if (checkIsMobile) {
      return;
    }

    const ctx = gsap.context(() => {
      const cards = cardsRef.current;
      // Reduced scroll distance on mobile for snappier transitions
      const totalScroll = window.innerHeight * 4;

      // Find the chapter mark (title) in the work section
      const workSection = containerRef.current?.closest("#work");
      const chapterMark = workSection?.querySelector(".chapter-mark");

      // Pin the container with mobile-optimized scrub
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: `+=${totalScroll}`,
          pin: true,
          scrub: 1, // Faster response on mobile
          anticipatePin: 1,
          onUpdate: (self) => {
            // Update card states based on scroll progress
            // Extended duration to account for extra scroll time after last card
            // Last card ends at (PROJECTS.length - 1) + 0.5 = PROJECTS.length - 0.5
            // Plus 2 extra duration = PROJECTS.length + 1.5
            const totalDuration = PROJECTS.length + 1.5;

            const newStates = PROJECTS.map((_, index) => {
              // Calculate when this card should be minimized
              // Adjusted to match the earlier fade-out timing (index)
              // We add 0.1 buffer to ensure next card is actually visible
              const nextCardStartTime = index + 1 + 0.1;
              const threshold = nextCardStartTime / totalDuration;

              let isMinimized;

              if (index < PROJECTS.length - 1) {
                // Normal cards minimize when next card comes up and content fades out
                isMinimized = self.progress > threshold;
              } else {
                // Last card minimizes earlier to allow tag expansion
                // Last card animation ends at: (PROJECTS.length - 1) + 0.5 = PROJECTS.length - 0.5
                // Minimize earlier, after some scroll time: PROJECTS.length - 0.5 + 0.5 = PROJECTS.length
                const lastCardMinimizeTime = PROJECTS.length;
                const lastCardThreshold = lastCardMinimizeTime / totalDuration;
                isMinimized = self.progress > lastCardThreshold;
              }

              return { isMinimized };
            });

            // Throttle state updates: only update if state actually changed
            const statesChanged = newStates.some(
              (state, i) =>
                !prevStatesRef.current ||
                state.isMinimized !== prevStatesRef.current[i]?.isMinimized,
            );

            if (statesChanged) {
              prevStatesRef.current = newStates;
              setCardStates(newStates);
            }
          },
        },
      });

      // Pin the chapter mark (title) to stay fixed while scrolling through cards
      if (chapterMark) {
        ScrollTrigger.create({
          trigger: containerRef.current,
          start: "top top",
          end: `+=${totalScroll}`,
          onEnter: () => {
            gsap.set(chapterMark, {
              position: "fixed",
              top: "40px",
              left: "60px",
              zIndex: 10,
            });
          },
          onLeave: () => {
            gsap.set(chapterMark, { clearProps: "all" });
          },
          onEnterBack: () => {
            gsap.set(chapterMark, {
              position: "fixed",
              top: "40px",
              left: "60px",
              zIndex: 10,
            });
          },
          onLeaveBack: () => {
            gsap.set(chapterMark, { clearProps: "all" });
          },
        });
      }

      // Animate cards
      cards.forEach((card, index) => {
        if (index === 0) return;

        gsap.set(card, { y: window.innerHeight });

        tl.to(
          card,
          {
            y: 30 * index,
            ease: "none",
            duration: 1,
          },
          index - 0.5,
        );

        const prevCard = cards[index - 1];
        if (prevCard) {
          // Fade out both header and body at the same time
          // Trigger even earlier (index - 0.3) so content is fully gone before card locks
          tl.to(
            prevCard.querySelector(".work-card-header-row"),
            {
              opacity: 0,
              duration: 0.5,
            },
            index - 0.3,
          );

          tl.to(
            prevCard.querySelector(".work-card-body"),
            {
              opacity: 0,
              duration: 0.5,
            },
            index - 0.3,
          );
        }
      });

      // Add extra scroll time after the last card
      // This creates additional scroll space before transitioning to Team section
      const lastCardIndex = PROJECTS.length - 1;
      // Last card animation ends at: (lastCardIndex - 0.5) + 1 = lastCardIndex + 0.5
      // Add 2 seconds of extra scroll time at that position
      tl.to({}, { duration: 2 }, lastCardIndex + 0.5);

      // Last card content stays visible - no fade out animation
    }, containerRef);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to scroll to a specific card (expand it)
  const scrollToCard = (cardIndex) => {
    const container = containerRef.current;
    if (!container) return;

    // Get the ScrollTrigger instance
    const triggers = ScrollTrigger.getAll();
    const trigger = triggers.find((st) => st.trigger === container);

    if (!trigger) return;

    // Calculate the scroll position to show this card fully expanded
    // Based on the timeline logic: Card i is fully visible at time i + 0.5
    // Total duration matches the timeline: PROJECTS.length + 1.5
    const totalDuration = PROJECTS.length + 1.5;

    // Target exactly exactly where the card locks into position (index + 0.5)
    // This ensures no 1px-2px gap remains that would show the previous card's body
    const targetTime = Math.max(0, cardIndex + 0.5);
    const targetProgress = targetTime / totalDuration;

    const scrollStart = trigger.start;
    const scrollEnd = trigger.end;
    const totalScrollDistance = scrollEnd - scrollStart;
    const targetScroll = scrollStart + totalScrollDistance * targetProgress;

    // Very fast scroll to position
    gsap.to(window, {
      scrollTo: targetScroll,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  return (
    <div className="work-stacking-cards" ref={containerRef}>
      {PROJECTS.map((project, index) => {
        const cardNumber = String(index + 1).padStart(2, "0");
        const tabLabel = `${cardNumber} | ${project.title}`;
        const isMobile = window.innerWidth <= 768;

        return (
          <div
            key={project.id}
            ref={(el) => (cardsRef.current[index] = el)}
            className={`work-card ${
              cardStates[index]?.isMinimized ? "minimized" : ""
            }`}
            style={{
              zIndex: index + 1,
            }}
          >
            {/* Orange Tag - Shows number when expanded, title when minimized (desktop only) */}
            <div
              className={`work-card-folder-tab ${
                cardStates[index]?.isMinimized ? "minimized" : ""
              }`}
              onClick={() =>
                cardStates[index]?.isMinimized && scrollToCard(index)
              }
            >
              {isMobile ? (
                // Mobile: Always show full title
                <span>{tabLabel}</span>
              ) : cardStates[index]?.isMinimized ? (
                // Desktop: Show full title when minimized
                <FadeInLabel
                  text={tabLabel}
                  isActive={cardStates[index]?.isMinimized}
                />
              ) : (
                // Desktop: Show only number when expanded
                <span>{cardNumber}</span>
              )}
            </div>

            <div className="work-card-inner">
              <div className="work-card-header-row">
                <div className="work-card-header-text">
                  <h2 className="work-card-title">{project.title}</h2>
                  <p className="work-card-subtitle">{project.subtitle}</p>
                </div>
                <button
                  className="work-card-arrow"
                  aria-label="View project"
                  onClick={() => openOverlay(project)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </button>
              </div>

              <div className="work-card-body">
                {/* Limited Media Grid: max 1 item (video if available, otherwise image) */}
                {(() => {
                  const limitedMedia = getLimitedMedia(project.images);
                  return (
                    <div
                      className={`work-card-media-grid work-card-media-grid-${limitedMedia.length}`}
                    >
                      {limitedMedia.map((mediaSrc, imgIndex) => (
                        <div
                          key={imgIndex}
                          className="work-card-media-container"
                          onClick={() => openOverlay(project)}
                        >
                          {isVideoFile(mediaSrc) ? (
                            <video
                              className="work-card-media"
                              src={mediaSrc}
                              autoPlay
                              loop
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              className="work-card-media"
                              src={mediaSrc}
                              alt={`${project.title} - Media ${imgIndex + 1}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}
      {/* Project Overlay */}
      <ProjectOverlay project={activeProject} onClose={closeOverlay} />
    </div>
  );
};

export default WorkStackingCards;
